import { parseRRule } from "./rrule-codec.js";
//#region src/components/RRule/utils/wall-clock-time.ts
/**
* Wall-clock helpers for RRULEs that are interpreted in a *server-side*
* timezone (e.g. a caller using the Region timezone). The picked local
* time must land in the RRULE verbatim — no UTC conversion.
*/
function timePickToWallClock(iso) {
	const date = new Date(iso);
	return {
		byhour: date.getHours(),
		byminute: date.getMinutes(),
		bysecond: 0
	};
}
/** Atalaya DateTimePicker ISO value -> wall-clock time of day. */
function timePickToTimeOfDay(iso) {
	const { byhour, byminute } = timePickToWallClock(iso);
	return {
		hour: byhour,
		minute: byminute
	};
}
/** RRule parsed options store BYHOUR/BYMINUTE as a number or a number[]. */
function firstClockPart(value, fallback) {
	if (Array.isArray(value)) return typeof value[0] === "number" ? value[0] : fallback;
	return typeof value === "number" ? value : fallback;
}
function addMinutesToTimeOfDay(hour, minute, durationMin) {
	const end = ((hour * 60 + minute + durationMin) % 1440 + 1440) % 1440;
	return {
		hour: Math.floor(end / 60),
		minute: end % 60
	};
}
/**
* Minutes from start to end on a 24h clock. Same clock time is a full-day
* (1440) window. End before start wraps overnight.
*/
function durationMinBetweenTimes(start, end) {
	const startMin = start.hour * 60 + start.minute;
	const diff = end.hour * 60 + end.minute - startMin;
	return diff <= 0 ? diff + 1440 : diff;
}
function wallClockToTimePickValue(byhour, byminute) {
	const date = /* @__PURE__ */ new Date();
	date.setHours(byhour, byminute, 0, 0);
	return date.toISOString();
}
function startTimeOfDayFromRRuleString(rrule) {
	if (!/BYHOUR=/i.test(rrule)) return null;
	const parsed = parseRRule(rrule);
	if (!parsed) return null;
	return {
		hour: parsed.byHour,
		minute: parsed.byMinute
	};
}
function formatClockHours(hours24, byminute) {
	const min = `${byminute || 0}`.padStart(2, "0");
	return hours24.map((hour24) => {
		return `${(hour24 + 11) % 12 + 1}:${min}${hour24 > 11 ? "pm" : "am"}`;
	}).join(", ");
}
/**
* Start/end clock labels for an RRULE that carries a window length. Returns
* `null` when the range would be ambiguous or meaningless — a rule with several
* BYHOUR starts, or a non-positive duration — so callers can fall back to the
* bare start-time display.
*/
function formatRRuleWallClockRange(byhour, byminute, durationMin) {
	if (byhour?.length !== 1 || !(durationMin > 0)) return null;
	const startHour = byhour[0];
	const startMin = startHour * 60 + (byminute || 0);
	const endMin = ((startMin + durationMin) % 1440 + 1440) % 1440;
	return {
		end: formatClockHours([Math.floor(endMin / 60)], endMin % 60),
		start: formatClockHours([startHour], byminute),
		wraps: startMin + durationMin >= 1440
	};
}
//#endregion
export { addMinutesToTimeOfDay, durationMinBetweenTimes, firstClockPart, formatClockHours, formatRRuleWallClockRange, startTimeOfDayFromRRuleString, timePickToTimeOfDay, timePickToWallClock, wallClockToTimePickValue };

//# sourceMappingURL=wall-clock-time.js.map